<template>
  <div class="flex items-center gap-2">
    <div class="avatar avatar-placeholder h-8">
      <div
        class="h-8 w-8 rounded-full text-black dark:text-white"
        :class="hashedColorClass"
        :style="hashedColorStyle"
      >
        <span>{{ username?.[0]?.toUpperCase() || '?' }}</span>
      </div>
    </div>
    <div class="flex flex-col overflow-hidden text-left whitespace-nowrap">
      <span class="max-w-full overflow-hidden text-xs font-semibold text-ellipsis lg:text-sm">
        {{ displayName ?? $t('global.unknown') }}
      </span>
      <div
        class="divide-base-content/50 text-base-content/50 max-w-32 min-w-full divide-x truncate text-xs"
        :title="roles.map((role) => $t(`user.role['${role}'].title`, '')).join(', ')"
      >
        <span
          v-for="role in roles"
          :key="role"
          class="text-xs not-first:pl-1 not-last:pr-1"
          :class="!noColor ? getUserRoleClass(UserRole[role], 'text') : undefined"
        >
          <FontAwesomeIcon :icon="getUserRoleIcon(UserRole[role])" class="mr-0.5" />
          <span v-if="$t(`user.role['${role}'].title`, '')" class="max-lg:hidden">
            {{ $t(`user.role['${role}'].title`) }}
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { UserRole, type RefUserDto } from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { computed } from 'vue';
import {
  HashedColorThemeAvatar,
  useHashedBackgroundColor,
} from '../../composables/style/useHashedBackgroundColor';
import { useUserRoleInfo } from '../../composables/user/useUserRoleInfo';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = withDefaults(
  defineProps<{
    user?: RefUserDto;
    noColor?: boolean;
    roleOrder?: UserRole[];
  }>(),
  {
    roleOrder: () => [
      UserRole.ADMIN,
      UserRole.OFFER_REVIEW,
      UserRole.HANDLING_REVIEW,
      UserRole.USER,
      UserRole.ITEM_HANDLING,
      UserRole.READONLY,
      UserRole.BETA_TESTER,
      UserRole.NONE,
    ],
  },
);

const username = computed(() => props.user?.username);
const displayName = computed(() =>
  props.user?.displayName && props.user?.displayName.trim().length > 0
    ? props.user?.displayName
    : props.user?.username,
);
const roles = computed(() =>
  props.user?.roles
    ? [...props.user.roles]
        .filter((role) => role !== 'BETA_TESTER')
        .sort((a, b) => {
          const order = props.roleOrder!;
          return order.indexOf(UserRole[a]) - order.indexOf(UserRole[b]);
        })
    : [],
);
const { getUserRoleClass, getUserRoleIcon } = useUserRoleInfo('text');

const { hashedColorClass, hashedColorStyle } = useHashedBackgroundColor(
  username,
  HashedColorThemeAvatar,
);
</script>
